import { format, formatDistanceToNowStrict, parseISO } from "date-fns";

export function fmtCurrency(
  amount: number | null | undefined,
  currency = "THB",
  opts: { compact?: boolean } = {}
): string {
  if (amount == null || Number.isNaN(amount)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: opts.compact ? 0 : 2,
      notation: opts.compact ? "compact" : "standard",
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function fmtNumber(n: number | null | undefined, opts: { compact?: boolean } = {}): string {
  if (n == null || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    notation: opts.compact ? "compact" : "standard",
    maximumFractionDigits: opts.compact ? 1 : 0,
  }).format(n);
}

function toDate(value: number | string | Date | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    // unix ms or s
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    // try numeric
    if (/^\d+$/.test(trimmed)) return toDate(Number(trimmed));
    // Domain timestamps are zone-less UTC text ("YYYY-MM-DD HH:MM:SS");
    // parseISO would read them as local time.
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      const d = new Date(`${trimmed.replace(" ", "T")}Z`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    try {
      const d = parseISO(trimmed);
      if (!Number.isNaN(d.getTime())) return d;
    } catch {
      /* noop */
    }
    const d2 = new Date(trimmed);
    return Number.isNaN(d2.getTime()) ? null : d2;
  }
  return null;
}

export function fmtDate(value: number | string | Date | null | undefined, fmt = "MMM d, yyyy"): string {
  const d = toDate(value);
  if (!d) return "—";
  try {
    return format(d, fmt);
  } catch {
    return "—";
  }
}

export function fmtDateTime(value: number | string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  try {
    return format(d, "MMM d, yyyy 'at' HH:mm:ss");
  } catch {
    return "—";
  }
}

export function fmtRelative(value: number | string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  try {
    return `${formatDistanceToNowStrict(d, { addSuffix: true })}`;
  } catch {
    return "—";
  }
}

export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || Number.isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(2)}s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)}m`;
  return `${(m / 60).toFixed(1)}h`;
}

export function fmtBytes(b: number | null | undefined): string {
  if (b == null || Number.isNaN(b)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function fmtPercent(n: number | null | undefined, decimals = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${(n * 100).toFixed(decimals)}%`;
}

export function truncate(s: string | null | undefined, max = 60): string {
  if (!s) return "";
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export function statusToTone(status: number | null | undefined): "success" | "info" | "warn" | "error" | "neutral" {
  if (status == null) return "neutral";
  if (status >= 500) return "error";
  if (status >= 400) return "warn";
  if (status >= 300) return "info";
  if (status >= 200) return "success";
  return "neutral";
}

export function dateToAbsoluteTitle(value: number | string | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "";
  return d.toISOString();
}
